import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { formatDate } from '../../lib/format'
import { ASK_WORDS, CURRENCY_WORDS, LINT_WORDS, provenanceWords, scoreWidth, shapeLine, silenceWords } from '../../lib/workbench'
import type { Callout, ChatTurn, Currency, GraphNode, LintFlag, MailHistory, MailThread, Opportunity, Pick, Provenance, TurnEffects, WorkbenchDraft } from '../../types/workbench'
import { LineIcon } from '../../components/LineIcon'

// The workbench's pieces, presentation only. Copy is a rep's: "still there", "two dates and a format",
// "It's in your Outbox". The API's vocabulary (askShape, lint kinds, currency states) never shows bare.
// Nothing here fetches; nothing here sends.

/** Where a fact came from: source · date · note, in one line. */
export function ProvenanceLine({ p, className }: { p?: Provenance; className?: string }) {
  if (!p) return null
  return <span className={`provenance ${className ?? ''}`}>{provenanceWords(p, formatDate)}</span>
}

/** Whether the person is still where the file says, and why we think so. */
export function CurrencyBadge({ currency }: { currency: Currency }) {
  return (
    <span className={`currency ${currency.state}`} title={`${currency.why} · ${provenanceWords(currency.provenance, formatDate)}`}>
      {CURRENCY_WORDS[currency.state]}
    </span>
  )
}

/** The rank as a ring: the score's share of the circle, the number inside. */
export function ScoreRing({ score, rank }: { score: number; rank: number }) {
  const r = 22, c = 2 * Math.PI * r
  const share = scoreWidth(score) / 100
  return (
    <svg className="score-ring" viewBox="0 0 56 56" width="56" height="56" role="img" aria-label={`rank ${rank}, ${Math.round(score * 100)} of 100`}>
      <circle className="ring-track" cx="28" cy="28" r={r} />
      <circle className="ring-fill" cx="28" cy="28" r={r} strokeDasharray={`${(share * c).toFixed(1)} ${c.toFixed(1)}`} transform="rotate(-90 28 28)" />
      <text className="ring-rank" x="28" y="28" textAnchor="middle" dominantBaseline="central">{rank}</text>
    </svg>
  )
}

/** One person the picker proposes: the rank as a ring, the reasons, the vetoes to read first, and "Not now".
 *  Hovering lights the same person on the field above. */
export function PickCard({ pick, rank, alternate = false, lit = false, onOpen, onLight, onCut, busy = false }: {
  pick: Pick; rank: number; alternate?: boolean; lit?: boolean; onOpen: () => void; onLight?: (on: boolean) => void; onCut?: (reason: string) => void; busy?: boolean
}) {
  return (
    <li className={`pick-card ${alternate ? 'alternate' : ''} ${lit ? 'lit' : ''} lane-${pick.lane} ${pick.vetoes.length > 0 ? 'vetoed' : ''}`}
      onMouseEnter={() => onLight?.(true)} onMouseLeave={() => onLight?.(false)}>
      <div className="pick-head">
        <ScoreRing score={pick.score} rank={rank} />
        <div className="pick-who">
          <button type="button" className="link-button pick-open" onClick={onOpen}>
            <strong>{pick.label}</strong>
          </button>
          <span className="pick-company">{pick.company}</span>
          <span className="pick-tags">
            <span className={`tag lane ${pick.lane}`}>{pick.lane === 'opportunity' ? 'an opening' : 'the relationship'}</span>
            {alternate && <span className="tag alternate">next in line</span>}
          </span>
        </div>
      </div>
      <ul className="plain pick-reasons">{pick.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
      {pick.vetoes.length > 0 && (
        <p className="pick-vetoes">{pick.vetoes.map((v, i) => <span key={i} className="tag veto" title={v.kind}>{v.text}</span>)}</p>
      )}
      <div className="pick-foot">
        <button type="button" className="button small primary" onClick={onOpen}>Work this one</button>
        {onCut && <NotNow onCut={onCut} busy={busy} />}
      </div>
    </li>
  )
}

/** "Not now", with the reason the picker keeps so the person is not proposed again until it changes. */
export function NotNow({ onCut, busy }: { onCut: (reason: string) => void; busy: boolean }) {
  return (
    <form className="not-now" onSubmit={(e) => { e.preventDefault(); const f = e.currentTarget; const r = (f.elements.namedItem('reason') as HTMLInputElement).value.trim(); if (r) onCut(r) }}>
      <input name="reason" type="text" placeholder="Not now, because…" aria-label="Why not now" disabled={busy} />
      <button type="submit" className="button small" disabled={busy}>Not now</button>
    </form>
  )
}

/** One opening at the account, selectable; a proposal until "Make this an opportunity" stores it (REQ-121). */
export function OpportunityCard({ o, selected = false, onSelect, onMake }: { o: Opportunity; selected?: boolean; onSelect?: () => void; onMake?: () => void }) {
  return (
    <li className={`opportunity-card lane-${o.lane} ${selected ? 'on' : ''}`}>
      <button type="button" className="opportunity-pick" onClick={onSelect} disabled={!onSelect} aria-pressed={selected}>
        <span className="opportunity-head">
          <strong>{o.label}</strong>
          <span className={`tag lane ${o.lane}`}>{o.lane === 'opportunity' ? 'an opening' : 'the relationship'}</span>
        </span>
        <span className="small">{o.situation}</span>
        <span className="muted small">{[o.serviceLine, o.people.length > 0 ? o.people.join(', ') : null].filter(Boolean).join(' · ')}</span>
        <ProvenanceLine p={o.provenance} className="small" />
      </button>
      {onMake && <button type="button" className="link-button small opportunity-make" onClick={onMake} title="Sets it up as an opportunity of yours, filled from this reading, so the messages and the plays gather under it.">Make this an opportunity</button>}
    </li>
  )
}

/** What a turn did, in chips: selected someone, started research, revised the draft, learned a rule, handed to the Outbox. */
export function EffectChips({ effects, nameOf }: { effects?: TurnEffects; nameOf?: (id: string) => string }) {
  if (!effects) return null
  const chips: ReactNode[] = []
  if (effects.selected) chips.push(<span key="sel" className="effect">selected {nameOf ? nameOf(effects.selected) : effects.selected}</span>)
  if (effects.research) chips.push(<span key="res" className="effect">researching</span>)
  if (effects.opportunity) chips.push(<span key="opp" className="effect">working an opening</span>)
  if (effects.draftRevised) chips.push(<span key="rev" className="effect">draft revised</span>)
  if (effects.voiceCorrectionId) chips.push(<span key="learn" className="effect learned">learned: never say that again</span>)
  if (effects.handedToOutbox) chips.push(<span key="out" className="effect outbox">in your Outbox</span>)
  return chips.length > 0 ? <span className="effects">{chips}</span> : null
}

/** One turn of the conversation: the rep on the right, Scout on the left, effects as chips beneath. */
export function TurnRow({ turn, nameOf }: { turn: ChatTurn; nameOf?: (id: string) => string }) {
  return (
    <li className={`turn ${turn.role}`}>
      <div className="turn-bubble">
        <p>{turn.text}</p>
        {turn.effects?.handedToOutbox && <p className="small outbox-line">It's in your <Link to="/outbox">Outbox</Link> — send it from there.</p>}
      </div>
      <div className="turn-meta">
        <span className="muted small">{turn.role === 'rep' ? 'you' : 'Scout'} · {formatDate(turn.at)}</span>
        <EffectChips effects={turn.effects} nameOf={nameOf} />
      </div>
    </li>
  )
}

/** One thing the lint found, in a rep's words; click to jump to the span. */
export function LintChip({ flag, onClick, active = false }: { flag: LintFlag; onClick?: () => void; active?: boolean }) {
  return (
    <button type="button" className={`lint ${flag.kind} ${active ? 'on' : ''}`} onClick={onClick} disabled={!onClick}>
      <strong>{LINT_WORDS[flag.kind] ?? flag.kind}</strong>
      <span>{flag.text}</span>
    </button>
  )
}

/** The evidence behind one part of the draft: the line, where it came from, and why the play says so. */
export function CalloutNote({ c }: { c: Callout }) {
  return (
    <li className="callout">
      <span>{c.text}</span>
      {c.provenance && <ProvenanceLine p={c.provenance} className="small" />}
      {c.tactic && <span className="muted small">why: {c.tactic}</span>}
    </li>
  )
}

/** The chips over a draft: the reason, the play, the shape of the ask, the day the dates were computed for. */
export function DraftChips({ draft }: { draft: WorkbenchDraft }) {
  return (
    <p className="draft-chips">
      <span className="tag reason">{draft.reason}</span>
      {draft.tacticName && <span className="tag play" title={draft.tacticWhy ?? undefined}>Play: {draft.tacticName}</span>}
      <span className="tag ask">{ASK_WORDS[draft.askShape] ?? draft.askShape}</span>
      <span className="tag computed" title="The dates in the ask were computed for this day; a draft older than a week is re-dated before it goes.">dated {formatDate(draft.computedOn)}</span>
    </p>
  )
}

/** The icon a channel is drawn as (REQ-105): mail, Teams, or a plain chat bubble for anything else. */
export function channelIcon(channel: string | undefined): { name: string; word: string } {
  const c = (channel ?? 'email').toLowerCase()
  if (c === 'email' || c === 'mail') return { name: 'mail', word: 'email' }
  if (c === 'teams') return { name: 'teams', word: 'Teams' }
  return { name: 'chat', word: c }
}

/** Every message exchanged with this person, or the honest line about what was searched when there is none. Each thread
 *  carries a preview control drawn as its channel; pressing it opens the thread's messages in an overlay (REQ-105). */
export function MailHistoryPanel({ mail, onPreview }: { mail: MailHistory; onPreview?: (thread: MailThread) => void }) {
  const cov = mail.coverage
  const where = `${cov.addresses.length === 0 ? 'no address on file' : cov.addresses.join(', ')} · ${formatDate(cov.from)} to ${formatDate(cov.to)}`
  if (mail.noneFound) {
    return (
      <div className="mail-history none">
        <p className="small"><strong>No mail with them.</strong> Searched {where}.</p>
        <p className="muted small">Nothing to lean on: the first message opens on how you know each other, not on a thread.</p>
      </div>
    )
  }
  return (
    <div className="mail-history">
      <p className="muted small">Searched {where}.{mail.silences > 0 ? ` ${silenceWords(mail.silences)}.` : ''}</p>
      <ul className="plain mail-threads">
        {mail.threads.map((t, i) => (
          <li key={i} className="mail-thread">
            {(() => { const ic = channelIcon(t.channel); return (
              <button type="button" className={`link-button thread-preview channel-${ic.name}`} onClick={onPreview ? () => onPreview(t) : undefined} disabled={!onPreview}
                title={`Preview this ${ic.word} thread`} aria-label={`Preview the ${ic.word} thread "${t.subject}"`}>
                <LineIcon name={ic.name} size={18} />
              </button>
            ) })()}
            <strong>{t.subject}</strong>
            <span className="muted small"> · {t.direction === 'own' ? 'you wrote last' : 'they wrote last'} · {formatDate(t.lastAt)}{t.unansweredFollowUps > 0 ? ` · ${t.unansweredFollowUps} of yours unanswered` : ''}</span>
            {t.engagementId && <Link className="small" to={`/opportunities/${encodeURIComponent(t.engagementId)}`}> on the pursuit</Link>}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** A shape from the stage, opened beside the conversation (REQ-103): what it is, its facts and its source, and one
 *  action that puts the words to fold it into the draft into the conversation box. The subject does not change. */
/** What the research found about the account and the person (TFY-32, 2026-10-08): the findings used to be drawn only on the
 *  diagram, which came off the crafting screen; this is their home now. Each carries where it came from, the outside ones a
 *  link to the page, and an aggregator-only one is marked not verified. A finding still being written shows as forming. */
export function FindingsPanel({ nodes, personId }: { nodes: GraphNode[]; personId?: string | null }) {
  const findings = nodes.filter((n): n is Extract<GraphNode, { kind: 'finding' }> => n.kind === 'finding')
  if (findings.length === 0) return null
  const forming = findings.filter((f) => f.materializing)
  const landed = findings.filter((f) => !f.materializing)
  return (
    <>
      <h3 className="panel-subtitle">What the research found{personId ? '' : ' about this account'}</h3>
      {forming.length > 0 && <p className="muted small researching" role="status">{forming.map((f) => f.label).join(' · ')}</p>}
      {landed.length > 0 && (
        <ul className="plain wb-findings">
          {landed.map((f) => (
            <li key={f.id} className={f.unverified ? 'unverified' : ''}>
              <span className="finding-text">{f.text || f.label}</span>
              <span className="finding-where">
                <ProvenanceLine p={f.provenance} />
                {f.provenance?.reference && /^https?:\/\//.test(f.provenance.reference) && <> · <a href={f.provenance.reference} target="_blank" rel="noreferrer">open the page</a></>}
                {f.unverified && <> · <span className="warn">not verified</span></>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

export function ShapeCard({ node, onIncorporate, onClose, busy = false }: { node: GraphNode; onIncorporate: () => void; onClose: () => void; busy?: boolean }) {
  const kind = node.kind === 'prior-work' ? 'prior work' : node.kind
  const prov = 'provenance' in node ? node.provenance : undefined
  return (
    <div className="shape-card" role="region" aria-label={`${kind}: ${node.label}`}>
      <div className="shape-head">
        <span className={`tag shape-kind ${node.kind}`}>{kind}</span>
        <button type="button" className="link-button shape-close" onClick={onClose} aria-label="Close">×</button>
      </div>
      <strong className="shape-title">{node.label}</strong>
      <span className="small">{shapeLine(node, formatDate)}</span>
      {prov && <ProvenanceLine p={prov} className="small" />}
      {node.kind === 'note' && node.text && <p className="shape-excerpt small">{node.text}</p>}
      {node.kind === 'note' && prov?.reference && <a className="small" href={prov.reference} target="_blank" rel="noopener noreferrer">Open in OneNote</a>}
      {node.kind === 'finding' && node.unverified && <span className="warn small">not verified — confirm before it goes in a message</span>}
      <div className="actions">
        <button type="button" className="button primary small" onClick={onIncorporate} disabled={busy} title="Puts the words to fold this into the draft into the conversation box; you read them, change them, and send them.">Incorporate into draft</button>
      </div>
    </div>
  )
}

/** The refusal shell every panel uses when the API says no. */
export function Refusal({ title, message, children }: { title: string; message: string; children?: ReactNode }) {
  return <div className="refusal"><strong>{title}</strong><p>{message}</p>{children}</div>
}
